import type { Event } from '@/types/event';
import { SourceType } from '@/types/source';
import {
    RangeOrigin,
    TimeDomain,
    type EventPlaybackRange,
    type LoopWindow,
} from '@/types/video-player';
import { getEventStartTime } from './event-duration';

const CAMERA_LOOP_PADDING_SECONDS = 3;
const MIN_LOOP_DURATION_SECONDS = 2;
const RECENT_EVENT_THRESHOLD_SECONDS = 30;
const PLAYBACK_LEAD_IN_SECONDS = 3;

export function isEventStillProcessing(
    event: Event | undefined,
    currentTimestamp: number,
): boolean {
    if (!event) return false;

    const latestFrameTime =
        event.sourceType === SourceType.FILE
            ? 0
            : (event.frames?.at(-1)?.time ?? 0);
    const latestActivity = Math.max(
        event.createdAt,
        event.updatedAt,
        latestFrameTime,
    );
    return currentTimestamp - latestActivity < RECENT_EVENT_THRESHOLD_SECONDS;
}

interface DeriveEventPlaybackRangeParams {
    event: Event;
    sourceType: SourceType;
    focuses?: number[][];
}

export function getTimeDomain(sourceType: SourceType): TimeDomain {
    return sourceType === SourceType.CAMERA
        ? TimeDomain.ABSOLUTE
        : TimeDomain.MEDIA;
}

export function deriveEventPlaybackRange({
    event,
    sourceType,
    focuses,
}: DeriveEventPlaybackRangeParams): EventPlaybackRange | null {
    const domain = getTimeDomain(sourceType);

    if (event.type === 'SCENE') {
        const { startTime, endTime } = event;
        if (startTime === undefined || endTime === undefined) {
            return null;
        }
        return {
            domain,
            origin: RangeOrigin.DETECTION,
            start: startTime,
            end: endTime,
            playbackStart: startTime,
        };
    }

    const { frames } = event;
    if (!frames?.length) {
        return null;
    }

    return {
        domain,
        origin: RangeOrigin.DETECTION,
        start: frames[0].time,
        end: frames[frames.length - 1].time,
        playbackStart: getEventStartTime(frames, focuses),
    };
}

export function buildLoopWindow(range: EventPlaybackRange): LoopWindow {
    const isSelection = range.origin === RangeOrigin.SELECTION;
    const padding =
        !isSelection && range.domain === TimeDomain.ABSOLUTE
            ? CAMERA_LOOP_PADDING_SECONDS
            : 0;
    const leadIn = isSelection ? 0 : PLAYBACK_LEAD_IN_SECONDS;

    let startTime = range.start - padding;
    let endTime = range.end + padding;

    if (range.domain === TimeDomain.MEDIA) {
        startTime = Math.max(0, startTime);
    }

    if (endTime - startTime < MIN_LOOP_DURATION_SECONDS) {
        const center = (startTime + endTime) / 2;
        startTime = center - MIN_LOOP_DURATION_SECONDS / 2;
        if (range.domain === TimeDomain.MEDIA) {
            startTime = Math.max(0, startTime);
        }
        endTime = startTime + MIN_LOOP_DURATION_SECONDS;
    }

    let playbackStart = range.playbackStart - leadIn;
    let windowStart = Math.min(startTime, playbackStart);

    if (range.domain === TimeDomain.MEDIA) {
        playbackStart = Math.max(0, playbackStart);
        windowStart = Math.max(0, windowStart);
    }

    return {
        startTime: windowStart,
        endTime: Math.max(endTime, range.end),
        playbackStart,
        domain: range.domain,
    };
}
